<template>
  <div class="modal-overlay" v-if="isOpen" 
    @mousedown="handleOverlayMouseDown"
    @mouseup="handleOverlayMouseUp"
  >
    <div class="modal-content" @mousedown="handleContentMouseDown">
      <!-- カスタムアイコン（モーダル上部） -->
      <div class="icon-top-area">
        <div class="icon-top-wrapper">
          <div class="icon-top-container" @click="triggerIconUpload" title="クリックでアイコン画像を変更">
            <img 
              v-if="customIconPreview || bookmark.customIcon" 
              :src="customIconPreview || bookmark.customIcon" 
              alt="カスタムアイコン" 
              class="icon-top-preview"
            >
            <img 
              v-else-if="bookmark.iconUrl && bookmark.iconUrl !== '/bookmark.svg'" 
              :src="bookmark.iconUrl" 
              alt="ファビコン" 
              class="icon-top-preview"
            >
            <div v-else class="icon-top-placeholder">
              <span class="mdi mdi-image-plus-outline icon-top-placeholder-icon"></span>
            </div>
            <div class="icon-top-edit-badge">
              <span class="mdi mdi-pencil"></span>
            </div>
          </div>
          <button
            v-if="bookmark.url"
            type="button"
            class="btn-refetch-favicon"
            :disabled="isRefetchingFavicon"
            @click.stop="refetchFavicon"
          >
            <span v-if="isRefetchingFavicon" class="mdi mdi-loading mdi-spin"></span>
            <span v-else class="mdi mdi-refresh"></span>
            Favicon再取得
          </button>
        </div>
        <button 
          v-if="customIconPreview || bookmark.customIcon" 
          type="button" 
          class="icon-top-remove" 
          @click.stop="removeCustomIcon"
          title="カスタムアイコンを削除"
        >
          <span class="mdi mdi-close"></span>
        </button>
        <input 
          type="file" 
          ref="iconFileInput"
          @change="handleIconUpload"
          accept="image/jpeg,image/png"
          style="display: none"
        >
      </div>

      <h2>{{ editTile && !editTile.isEmpty ? 'ブックマーク編集' : 'ブックマーク追加' }}</h2>
      
      <div class="form-group">
        <label for="bookmark-url">URL <span class="required">*</span></label>
        <input 
          type="url" 
          id="bookmark-url" 
          ref="urlInput"
          v-model="bookmark.url" 
          placeholder="https://example.com"
          class="form-control"
          @keydown.enter="handleEnterKey"
        >
      </div>
      
      <div class="form-group">
        <label for="bookmark-name">名称</label>
        <input 
          type="text" 
          id="bookmark-name" 
          v-model="bookmark.name" 
          placeholder="サイト名（空欄の場合は自動取得します）"
          class="form-control"
          @keydown.enter="handleEnterKey"
        >
      </div>
      
      <div class="form-group">
        <label for="bookmark-color">背景色</label>
        <div class="color-chips">
          <!-- 無色（初期値に戻す） -->
          <div 
            class="color-chip no-color-chip" 
            :class="{ 'selected-chip': selectedChipIndex === -1 }"
            title="背景色なし"
            @click="selectNoColor"
          ></div>
          <!-- カラーパレット（全てcustomColors配列で管理） -->
          <div 
            v-for="(color, index) in customColors" 
            :key="'color-' + index"
            class="color-chip"
            :class="{ 'selected-chip': selectedChipIndex === index }" 
            :style="{ backgroundColor: color }"
            @click="selectChip(index)"
          ></div>
          <!-- カラー追加ボタン -->
          <div 
            v-if="customColors.length < maxPaletteColors"
            class="color-chip add-color-chip" 
            title="カラーを追加"
            @click="addColor"
          >
            <span class="mdi mdi-plus"></span>
          </div>
        </div>
        <input 
          type="color" 
          id="bookmark-color" 
          v-model="bookmark.color"
          class="form-control color-picker"
        >
      </div>
      

      
      <div class="modal-buttons">
        <button class="btn btn-cancel" @click="close">キャンセル</button>
        <!-- 編集時のみ削除ボタンを表示 -->
        <button 
          v-if="editTile && !editTile.isEmpty" 
          class="btn btn-delete" 
          @click="deleteTile"
        >削除</button>
        <button 
          class="btn btn-save" 
          :class="{ 'btn-active': isFormValid }"
          :disabled="!isFormValid" 
          @click="save"
        >保存</button>
      </div>
    </div>
    <!-- ネイティブalert代替トースト -->
    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="4000" location="bottom">
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { fetchSiteTitle, getFaviconUrlWithFallback } from '@/services/siteInfo';

export default {
  props: {
    isOpen: Boolean,
    editTile: Object,
    customColors: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      bookmark: {
        name: '',
        url: '',
        iconUrl: '',
        color: '#f0f0f0',
        customIcon: '' // カスタムアイコン（Base64）
      },
      isLoading: false,
      mouseDownOnOverlay: false, // 追加: 外側でmousedownされたか
      customIconPreview: '', // アップロードした画像のプレビュー
      isRefetchingFavicon: false,
      selectedChipIndex: -1, // 選択中のパレットインデックス（-1は「無色」）
      // ネイティブalert代替トースト
      snack: { show: false, text: '', color: 'error' }
    };
  },
  
  computed: {
    // フォームのバリデーション - URLが入力されているか確認
    isFormValid() {
      return this.bookmark.url && this.bookmark.url.trim() !== '';
    },
    colorPresets() {
      return ['#A7C6E2', '#B7D9BB', '#F3B6B1', '#F3D298', '#D2D2D2', '#E8A89E'];
    },
    maxPaletteColors() {
      return 12;
    }
  },
  
  watch: {
    'bookmark.color'(newColor) {
      // カラーピッカー変更時、選択中のパレットをリアルタイム更新
      if (this.selectedChipIndex >= 0 && this.selectedChipIndex < this.customColors.length) {
        if (this.customColors[this.selectedChipIndex] !== newColor) {
          const newColors = [...this.customColors];
          newColors[this.selectedChipIndex] = newColor;
          this.$emit('update-custom-colors', newColors);
        }
      }
    },
    isOpen(newVal) {
      if (newVal && this.editTile) {
        // モーダルが開かれたときにデータをセット
        this.bookmark = {
          name: this.editTile.isEmpty ? '' : this.editTile.name || '',
          url: this.editTile.url || '',
          iconUrl: this.editTile.isEmpty ? '' : this.editTile.iconUrl || '',
          color: this.editTile.color || '#f0f0f0',
          customIcon: this.editTile.isEmpty ? '' : this.editTile.customIcon || ''
        };
        
        // プレビューとチップ選択をリセット
        this.customIconPreview = '';
        this.selectedChipIndex = this.detectSelectedIndex(this.bookmark.color);
        
        // モーダルが開いた後にURLフィールドにフォーカスを当てる
        this.$nextTick(() => {
          if (this.$refs.urlInput) {
            this.$refs.urlInput.focus();
          }
        });
      }
    }
  },
  
  methods: {
    showSnack(text, color = 'error') {
      this.snack.text = text;
      this.snack.color = color;
      this.snack.show = true;
    },

    close() {
      this.customIconPreview = '';
      this.$emit('close');
    },
    
    // Enterキー押下時の処理
    handleEnterKey(e) {
      if (e.isComposing) return;
      // フォームがバリデート済みの場合のみ保存処理を実行
      if (this.isFormValid) {
        this.save();
      }
    },
    
    // オーバーレイのmousedown/mouseupハンドラ
    handleOverlayMouseDown(e) {
      if (e.target.classList.contains('modal-overlay')) {
        this.mouseDownOnOverlay = true;
      } else {
        this.mouseDownOnOverlay = false;
      }
    },
    
    handleOverlayMouseUp(e) {
      if (
        this.mouseDownOnOverlay &&
        e.target.classList.contains('modal-overlay')
      ) {
        this.close();
      }
      this.mouseDownOnOverlay = false;
    },
    
    // モーダル本体でmousedown時はフラグリセット
    handleContentMouseDown() {
      this.mouseDownOnOverlay = false;
    },
    
    async save() {
      // 入力値のバリデーション（URLのみ必須）
      if (!this.bookmark.url) {
        this.showSnack('URLを入力してください');
        return;
      }
      
      // URLの形式チェック（http:// または https:// で始まっていない場合は追加）
      let url = this.bookmark.url.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      
      // 名前が空欄の場合、APIを使用してサイト名を取得
      let name = this.bookmark.name;
      if (!name) {
        try {
          this.isSaving = true;
          console.log(`[BookmarkModal] サイトタイトル取得開始: ${url}`);
          name = await fetchSiteTitle(url);
          console.log(`[BookmarkModal] サイトタイトル取得結果: ${name}`);
          
          // 空の結果の場合はドメインを使用（fetchSiteTitle内部でフォールバック済み）
          if (!name || name.trim() === '') {
            const domain = new URL(url).hostname;
            name = domain.replace(/^www\./, '');
            console.log(`[BookmarkModal] ドメインベース名前: ${name}`);
          }
        } catch (error) {
          console.error('[BookmarkModal] サイトタイトル取得エラー:', error);
          // エラー発生時はドメインを使用
          try {
            const domain = new URL(url).hostname;
            name = domain.replace(/^www\./, '');
            console.log(`[BookmarkModal] エラー時ドメインベース名前: ${name}`);
          } catch (e) {
            console.error('[BookmarkModal] URL解析エラー:', e);
            name = 'ブックマーク';
          }
        } finally {
          this.isSaving = false;
        }
      }
      
      // カスタムアイコンの設定
      const customIcon = this.customIconPreview || this.bookmark.customIcon || '';
      
      // ファビコンURLの設定（カスタムアイコンがない場合のみ取得）
      let iconUrl;
      if (!customIcon) {
        const existingIconUrl = this.bookmark.iconUrl;
        const isNewBookmark = !this.editTile || this.editTile.isEmpty;
        const urlChanged = this.editTile && this.editTile.url !== url;
        
        if (!isNewBookmark && !urlChanged && existingIconUrl && existingIconUrl !== '/bookmark.svg') {
          // 既存ブックマークでURLが変わっていない場合、現在のアイコンを維持
          iconUrl = existingIconUrl;
          console.log(`[BookmarkModal] 既存ファビコンを維持: ${iconUrl.substring(0, 60)}...`);
        } else {
          // 新規追加またはURL変更時のみAPIから取得
          try {
            console.log(`[BookmarkModal] ファビコン取得開始: ${url}`);
            iconUrl = await getFaviconUrlWithFallback(url);
            console.log(`[BookmarkModal] ファビコン取得成功: ${iconUrl.substring(0, 60)}...`);
          } catch (e) {
            console.error(`[BookmarkModal] ファビコン取得エラー:`, e);
            iconUrl = '/bookmark.svg';
          }
        }
      } else {
        // カスタムアイコンがある場合は空文字を設定（カスタムアイコンを使用）
        iconUrl = '';
      }
      
      // 親コンポーネントに保存イベントを発行
      this.$emit('save', {
        name,
        url,
        iconUrl,
        color: this.bookmark.color,
        customIcon
      });
      
      // プレビューをクリア
      this.customIconPreview = '';
    },
    // 色から対応するパレットインデックスを検出
    detectSelectedIndex(color) {
      if (!color || color === '#f0f0f0') return -1;
      const lower = color.toLowerCase();
      const idx = this.customColors.findIndex(c => c.toLowerCase() === lower);
      return idx >= 0 ? idx : -1;
    },
    selectNoColor() {
      this.bookmark.color = '#f0f0f0';
      this.selectedChipIndex = -1;
    },
    selectChip(index) {
      this.selectedChipIndex = index;
      this.bookmark.color = this.customColors[index];
    },
    addColor() {
      if (this.customColors.length >= this.maxPaletteColors) return;
      const newColors = [...this.customColors, this.bookmark.color];
      this.$emit('update-custom-colors', newColors);
      this.selectedChipIndex = newColors.length - 1;
    },
    
    // Favicon再取得
    async refetchFavicon() {
      const url = this.bookmark.url?.trim();
      if (!url) return;
      let targetUrl = url;
      if (!/^https?:\/\//i.test(targetUrl)) {
        targetUrl = 'https://' + targetUrl;
      }
      this.isRefetchingFavicon = true;
      try {
        const iconUrl = await getFaviconUrlWithFallback(targetUrl);
        this.bookmark.iconUrl = iconUrl;
        this.bookmark.customIcon = '';
        this.customIconPreview = '';
        console.log(`[BookmarkModal] Favicon再取得完了: ${iconUrl.substring(0, 60)}...`);
      } catch (e) {
        console.error('[BookmarkModal] Favicon再取得エラー:', e);
      } finally {
        this.isRefetchingFavicon = false;
      }
    },

    // アイコンアップロードをトリガー
    triggerIconUpload() {
      this.$refs.iconFileInput.click();
    },
    
    // アイコンアップロード処理
    async handleIconUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // ファイル形式チェック
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        this.showSnack('JPGまたはPNG形式の画像を選択してください');
        event.target.value = '';
        return;
      }
      
      // ファイルサイズチェック（10MB上限）
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showSnack('ファイルサイズは10MB以下にしてください');
        event.target.value = '';
        return;
      }
      
      try {
        // 画像をリサイズしてBase64に変換
        const resizedBase64 = await this.resizeImage(file, 256, 256);
        this.customIconPreview = resizedBase64;
        console.log('[BookmarkModal] カスタムアイコンをアップロードしました');
      } catch (error) {
        console.error('[BookmarkModal] 画像処理エラー:', error);
        this.showSnack('画像の処理に失敗しました');
      }
      
      // ファイル入力をリセット（同じファイルを再度選択できるように）
      event.target.value = '';
    },
    
    // 画像をリサイズしてBase64に変換
    resizeImage(file, maxWidth, maxHeight) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const img = new Image();
          
          img.onload = () => {
            // Canvas を作成
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // 256x256 固定サイズに設定
            canvas.width = maxWidth;
            canvas.height = maxHeight;
            
            // 背景を透明にする（PNG対応）
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // アスペクト比を維持してセンタリング
            const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
            const width = img.width * scale;
            const height = img.height * scale;
            const x = (maxWidth - width) / 2;
            const y = (maxHeight - height) / 2;
            
            // 画像を描画
            ctx.drawImage(img, x, y, width, height);
            
            // 元のファイル形式を維持してBase64に変換
            // JPEG品質0.9は画質とファイルサイズのバランスを考慮した値
            // (高品質を維持しつつBase64サイズを抑制)
            const mimeType = file.type;
            const quality = mimeType === 'image/jpeg' ? 0.9 : undefined;
            const base64 = canvas.toDataURL(mimeType, quality);
            
            resolve(base64);
          };
          
          img.onerror = () => {
            reject(new Error('画像の読み込みに失敗しました'));
          };
          
          img.src = e.target.result;
        };
        
        reader.onerror = () => {
          reject(new Error('ファイルの読み込みに失敗しました'));
        };
        
        reader.readAsDataURL(file);
      });
    },
    
    // カスタムアイコンを削除
    removeCustomIcon() {
      this.customIconPreview = '';
      this.bookmark.customIcon = '';
    },
    
    // 削除機能
    deleteTile() {
      // close()は呼ばない。削除確認後のモーダルクローズはApp.vue側(deleteBookmarkFromModal)が担当する
      this.$emit('delete', this.editTile);
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start; /* centerからflex-startに変更して上部に配置 */
  padding-top: 10vh; /* 上部から少し空間を開ける */
  z-index: 1000;
  overflow-y: auto; /* スクロール可能に */
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.required {
  color: #ff4d4f;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn {
  padding: 8px 16px;
  margin-left: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-cancel {
  background-color: #f1f1f1;
  color: #333;
}

.btn-delete {
  background-color: #ff4d4f;
  color: white;
  transition: background-color 0.3s ease;
}

.btn-delete:hover {
  background-color: #ff1f23;
}

.btn-save {
  background-color: #cccccc; /* 非アクティブ時の色 */
  color: white;
  min-width: 80px;
  transition: background-color 0.3s ease;
}

.btn-active {
  background-color: #d9a89c; /* アクティブ時の色 */
}

.btn-save:disabled {
  cursor: not-allowed;
}

.color-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.color-chip {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.2s, border-color 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 無色チップ（点線丸） */
.no-color-chip {
  background-color: transparent !important;
  border: 2px dashed #bbb;
  box-shadow: none;
}

.no-color-chip:hover {
  border-color: #d9a89c;
}

/* 選択中のチップ */
.selected-chip {
  border-color: #333 !important;
  border-style: solid !important;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.2);
}

.color-chip:hover {
  transform: scale(1.1);
}

/* カスタムカラー追加ボタン */
.add-color-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ccc;
  background-color: transparent !important;
  color: #999;
  font-size: 18px;
}

.add-color-chip:hover {
  border-color: #d9a89c;
  color: #d9a89c;
}

.color-picker {
  height: 36px;
  padding: 0;
  margin-top: 5px;
}

/* カスタムアイコン（モーダル上部） */
.icon-top-area {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
  position: relative;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

.icon-top-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.btn-refetch-favicon {
  margin-top: 4px;
  padding: 2px 8px;
  font-size: 11px;
  color: #888;
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: color 0.2s, border-color 0.2s;
}

.btn-refetch-favicon:hover {
  color: #d9a89c;
  border-color: #d9a89c;
}

.btn-refetch-favicon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-top-container {
  width: 72px;
  height: 72px;
  border: 2px dashed #ccc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.icon-top-container:hover {
  border-color: #d9a89c;
  box-shadow: 0 0 0 3px rgba(233, 208, 202, 0.3);
}

.icon-top-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.icon-top-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-top-placeholder-icon {
  font-size: 28px;
  color: #bbb;
}

.icon-top-edit-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  background-color: #d9a89c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  pointer-events: none;
}

.icon-top-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #888;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  padding: 0;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: background-color 0.2s;
}

.icon-top-remove:hover {
  background-color: #ff4d4f;
}

/* モバイル向けのスタイル調整 */
@media (max-width: 600px) {
  .modal-overlay {
    align-items: flex-start;
    padding-top: 70px; /* ヘッダーの下に表示されるように固定値に変更 */
  }
  
  .modal-content {
    width: 95%; /* より幅を広く */
    max-height: 85vh; /* 高さ制限を設定 */
    overflow-y: auto; /* コンテンツが多い場合はスクロール */
    padding: 15px; /* パディングを少し減らす */
  }
  
  input[type="url"], 
  input[type="text"] {
    font-size: 16px; /* iOSでの自動ズームを防止 */
  }
  
  .form-group {
    margin-bottom: 12px; /* 間隔を少し縮める */
  }
  
  /* 色選択チップを小さくして表示を確保 */
  .color-chip {
    width: 28px;
    height: 28px;
  }
  
  /* カスタムアイコンのモバイル調整 */
  .icon-top-container {
    width: 64px;
    height: 64px;
  }

  .icon-top-placeholder-icon {
    font-size: 24px;
  }
}
</style>
